import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/crm/ClientList';
import ClientForm from './pages/crm/ClientForm';
import ClientDetail from './pages/crm/ClientDetail';
import SupplierList from './pages/crm/SupplierList';
import SupplierForm from './pages/crm/SupplierForm';
import SupplierDetail from './pages/crm/SupplierDetail';
import PackageList from './pages/packages/PackageList';
import PackageForm from './pages/packages/PackageForm';
import PackageDetail from './pages/packages/PackageDetail';
import BookingList from './pages/packages/BookingList';
import BookingForm from './pages/packages/BookingForm';
import TicketList from './pages/ticketing/TicketList';
import TicketForm from './pages/ticketing/TicketForm';
import VisaList from './pages/visas/VisaList';
import VisaForm from './pages/visas/VisaForm';
import AccountList from './pages/accounts/AccountList';
import AccountLedger from './pages/accounts/AccountLedger';
import BillList from './pages/bills/BillList';
import BillForm from './pages/bills/BillForm';
import BillDetail from './pages/bills/BillDetail';
import PaymentVoucherList from './pages/vouchers/PaymentVoucherList';
import PaymentVoucherForm from './pages/vouchers/PaymentVoucherForm';
import PaymentVoucherPrint from './pages/vouchers/PaymentVoucherPrint';
import ReceiptVoucherList from './pages/vouchers/ReceiptVoucherList';
import ReceiptVoucherForm from './pages/vouchers/ReceiptVoucherForm';
import ReceiptVoucherPrint from './pages/vouchers/ReceiptVoucherPrint';
import SuspenseRegister from './pages/vouchers/SuspenseRegister';
import ChequeDashboard from './pages/vouchers/ChequeDashboard';
import CommissionSetOff from './pages/commission/CommissionSetOff';
import CommissionTable from './pages/commission/CommissionTable';
import PackageOptions from './pages/packages/PackageOptions';
import CommissionLedger from './pages/commission/CommissionLedger';
import TrialBalance from './pages/finance/TrialBalance';
import ProfitLoss from './pages/finance/ProfitLoss';
import BalanceSheet from './pages/finance/BalanceSheet';
import JournalList from './pages/journals/JournalList';
import JournalForm from './pages/journals/JournalForm';
import JournalDetail from './pages/journals/JournalDetail';
import UserList from './pages/users/UserList';
import TermsAdmin from './pages/settings/TermsAdmin';
import CompanySettings from './pages/settings/CompanySettings';
import AgentPortal from './pages/agent/AgentPortal';
import ClientLedger from './pages/ledger/ClientLedger';
import SupplierLedger from './pages/ledger/SupplierLedger';
import AccountsReceivable from './pages/finance/AccountsReceivable';
import AccountsPayable from './pages/finance/AccountsPayable';
import CashBankDashboard from './pages/finance/CashBankDashboard';
import AgentList from './pages/agents/AgentList';
import AgentForm from './pages/agents/AgentForm';
import AgentDetail from './pages/agents/AgentDetail';
import StaffPerformance from './pages/agents/StaffPerformance';
import AgentSales from './pages/reports/AgentSales';
import TicketsIssued from './pages/reports/TicketsIssued';
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceNew, { CostingEntry } from './pages/invoices/InvoiceNew';
import InvoicePrint from './pages/invoices/InvoicePrint';
import CostingPending from './pages/invoices/CostingPending';
import QuotationList from './pages/quotations/QuotationList';
import QuotationForm from './pages/quotations/QuotationForm';
import QuotationPrint from './pages/quotations/QuotationPrint';
import SaleQuotationList from './pages/sale-quotations/SaleQuotationList';
import SaleQuotationForm from './pages/sale-quotations/SaleQuotationForm';
import SaleQuotationPrint from './pages/sale-quotations/SaleQuotationPrint';
import MobileLayout from './components/layout/MobileLayout';
import MobileDashboard from './pages/mobile/MobileDashboard';
import MobileInvoices from './pages/mobile/MobileInvoices';
import MobileProFormas from './pages/mobile/MobileProFormas';
import MobileQuotations from './pages/mobile/MobileQuotations';
import MobileClients from './pages/mobile/MobileClients';
import useIsMobile from './hooks/useIsMobile';
import './styles/global.css';

const CostingEntryRoute = () => { const { id } = useParams(); return <CostingEntry invoiceId={id} />; };

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" />;
};

const AgentRoute = ({ children }) => {
  // Also let a non-agent staff account through if it's linked to an agent record
  // (e.g. an admin like Lakmali who is also an agent) — role alone would otherwise
  // lock her own numbers behind a role she doesn't have, even though AgentPortal
  // itself is scoped by agentRefId, not role.
  const { user, loading, isAgent, agentRefId } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (!isAgent && !agentRefId) return <Navigate to="/" />;
  return children;
};

const TabRoute = ({ tabKey, children }) => {
  const { loading, canSee } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!canSee(tabKey)) return <Navigate to="/" />;
  return children;
};

// A limited agent can't see the 'clients' tab (the full company-wide list/detail pages
// stay admin-only), but should still be able to create a new client of their own —
// ClientForm.js itself locks care_of_agent_id to their own id when it detects this role.
const ClientCreateRoute = ({ children }) => {
  const { loading, canSee, role, agentRefId } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!canSee('clients') && !(role === 'agent' && agentRefId)) return <Navigate to="/" />;
  return children;
};

function MobileRedirect() {
  const isMobile = useIsMobile();
  if (isMobile) return <Navigate to="/m" replace />;
  return null;
}

// A password-recovery link logs someone in but shouldn't just drop them into the
// app — force the "set new password" screen first, ahead of every other route
// (including /login, in case the recovery tab already had a stale session).
function RecoveryGate({ children }) {
  const { passwordRecovery } = useAuth();
  if (passwordRecovery) return <ResetPassword />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter>
        <RecoveryGate>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Mobile routes */}
            <Route path="/m" element={<PrivateRoute><MobileLayout /></PrivateRoute>}>
              <Route index element={<MobileDashboard />} />
              <Route path="invoices" element={<MobileInvoices />} />
              <Route path="proformas" element={<MobileProFormas />} />
              <Route path="quotations" element={<MobileQuotations />} />
              <Route path="clients" element={<MobileClients />} />
            </Route>
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<TabRoute tabKey="dashboard"><MobileRedirect /><Dashboard /></TabRoute>} />

              <Route path="agents"             element={<TabRoute tabKey="agents"><AgentList /></TabRoute>} />
              <Route path="agents/new"         element={<TabRoute tabKey="agents"><AgentForm /></TabRoute>} />
              <Route path="agents/performance" element={<TabRoute tabKey="staff_performance"><StaffPerformance /></TabRoute>} />
              <Route path="reports/agent-sales" element={<TabRoute tabKey="agent_sales"><AgentSales /></TabRoute>} />
              <Route path="reports/tickets-issued" element={<TabRoute tabKey="tickets_report"><TicketsIssued /></TabRoute>} />
              <Route path="agents/:id"         element={<TabRoute tabKey="agents"><AgentDetail /></TabRoute>} />
              <Route path="agents/:id/edit"    element={<TabRoute tabKey="agents"><AgentForm /></TabRoute>} />

              <Route path="clients"          element={<TabRoute tabKey="clients"><ClientList /></TabRoute>} />
              <Route path="clients/new"      element={<ClientCreateRoute><ClientForm /></ClientCreateRoute>} />
              <Route path="clients/:id"      element={<TabRoute tabKey="clients"><ClientDetail /></TabRoute>} />
              <Route path="clients/:id/edit" element={<TabRoute tabKey="clients"><ClientForm /></TabRoute>} />

              <Route path="suppliers"          element={<TabRoute tabKey="suppliers"><SupplierList /></TabRoute>} />
              <Route path="suppliers/new"      element={<TabRoute tabKey="suppliers"><SupplierForm /></TabRoute>} />
              <Route path="suppliers/:id"      element={<TabRoute tabKey="suppliers"><SupplierDetail /></TabRoute>} />
              <Route path="suppliers/:id/edit" element={<TabRoute tabKey="suppliers"><SupplierForm /></TabRoute>} />

              <Route path="packages"     element={<TabRoute tabKey="packages"><PackageList /></TabRoute>} />
              <Route path="packages/new"       element={<TabRoute tabKey="packages"><PackageForm /></TabRoute>} />
              <Route path="packages/:id/edit" element={<TabRoute tabKey="packages"><PackageForm /></TabRoute>} />
              <Route path="packages/:id" element={<TabRoute tabKey="packages"><PackageDetail /></TabRoute>} />

              <Route path="bookings"     element={<TabRoute tabKey="bookings"><BookingList /></TabRoute>} />
              <Route path="bookings/new" element={<TabRoute tabKey="bookings"><BookingForm /></TabRoute>} />
              <Route path="bookings/:id" element={<TabRoute tabKey="bookings"><BookingForm /></TabRoute>} />

              <Route path="tickets"     element={<TabRoute tabKey="tickets"><TicketList /></TabRoute>} />
              <Route path="tickets/new" element={<TabRoute tabKey="tickets"><TicketForm /></TabRoute>} />
              <Route path="tickets/:id" element={<TabRoute tabKey="tickets"><TicketForm /></TabRoute>} />

              <Route path="visas"     element={<TabRoute tabKey="visas"><VisaList /></TabRoute>} />
              <Route path="visas/new" element={<TabRoute tabKey="visas"><VisaForm /></TabRoute>} />
              <Route path="visas/:id" element={<TabRoute tabKey="visas"><VisaForm /></TabRoute>} />

              <Route path="accounts"            element={<TabRoute tabKey="accounts"><AccountList /></TabRoute>} />
              <Route path="accounts/:id/ledger" element={<TabRoute tabKey="accounts"><AccountLedger /></TabRoute>} />

              <Route path="cash-bank" element={<TabRoute tabKey="cash_bank"><CashBankDashboard /></TabRoute>} />

              <Route path="bills"       element={<TabRoute tabKey="bills"><BillList /></TabRoute>} />
              <Route path="bills/new"   element={<TabRoute tabKey="bills"><BillForm /></TabRoute>} />
              <Route path="bills/:id"   element={<TabRoute tabKey="bills"><BillDetail /></TabRoute>} />
              <Route path="vouchers/payment"     element={<TabRoute tabKey="payment_vouchers"><PaymentVoucherList /></TabRoute>} />
              <Route path="vouchers/payment/new" element={<TabRoute tabKey="payment_vouchers"><PaymentVoucherForm /></TabRoute>} />
              <Route path="vouchers/payment/:id" element={<TabRoute tabKey="payment_vouchers"><PaymentVoucherPrint /></TabRoute>} />

              <Route path="vouchers/receipt"     element={<TabRoute tabKey="receipt_vouchers"><ReceiptVoucherList /></TabRoute>} />
              <Route path="vouchers/receipt/new" element={<TabRoute tabKey="receipt_vouchers"><ReceiptVoucherForm /></TabRoute>} />
              <Route path="vouchers/suspense"    element={<TabRoute tabKey="receipt_vouchers"><SuspenseRegister /></TabRoute>} />
              <Route path="vouchers/receipt/:id" element={<TabRoute tabKey="receipt_vouchers"><ReceiptVoucherPrint /></TabRoute>} />

              <Route path="vouchers/cheques" element={<TabRoute tabKey="cheque_dashboard"><ChequeDashboard /></TabRoute>} />
              <Route path="vouchers/deposit-cheques" element={<Navigate to="/vouchers/cheques" replace />} />

              <Route path="commissions/table"  element={<TabRoute tabKey="commission_table"><CommissionTable /></TabRoute>} />
              <Route path="commissions/setoff" element={<TabRoute tabKey="commission_setoff"><CommissionSetOff /></TabRoute>} />
              <Route path="commissions/ledger" element={<TabRoute tabKey="commissions_ledger"><CommissionLedger /></TabRoute>} />
              <Route path="settings/package-options" element={<TabRoute tabKey="package_options"><PackageOptions /></TabRoute>} />

              <Route path="finance/trial-balance" element={<TabRoute tabKey="trial_balance"><TrialBalance /></TabRoute>} />
              <Route path="finance/profit-loss"   element={<TabRoute tabKey="profit_loss"><ProfitLoss /></TabRoute>} />
              <Route path="finance/balance-sheet" element={<TabRoute tabKey="balance_sheet"><BalanceSheet /></TabRoute>} />
              <Route path="finance/ar"            element={<TabRoute tabKey="ar"><AccountsReceivable /></TabRoute>} />
              <Route path="finance/ap"            element={<TabRoute tabKey="ap"><AccountsPayable /></TabRoute>} />

              <Route path="journals"     element={<TabRoute tabKey="journals"><JournalList /></TabRoute>} />
              <Route path="journals/new" element={<TabRoute tabKey="journals"><JournalForm /></TabRoute>} />
              <Route path="journals/:id" element={<TabRoute tabKey="journals"><JournalDetail /></TabRoute>} />

              <Route path="ledger/clients"   element={<TabRoute tabKey="client_ledger"><ClientLedger /></TabRoute>} />
              <Route path="ledger/suppliers" element={<TabRoute tabKey="supplier_ledger"><SupplierLedger /></TabRoute>} />

              <Route path="invoices"                    element={<TabRoute tabKey="invoices"><InvoiceList /></TabRoute>} />
              <Route path="invoices/new"               element={<TabRoute tabKey="invoices"><InvoiceNew /></TabRoute>} />
              <Route path="invoices/costing-pending"   element={<TabRoute tabKey="invoices"><CostingPending /></TabRoute>} />
              <Route path="invoices/:id/costing"       element={<TabRoute tabKey="invoices"><CostingEntryRoute /></TabRoute>} />
              <Route path="invoices/:id"               element={<TabRoute tabKey="invoices"><InvoicePrint /></TabRoute>} />

              <Route path="sale-quotations"          element={<TabRoute tabKey="sale_quotations"><SaleQuotationList /></TabRoute>} />
              <Route path="sale-quotations/new"      element={<TabRoute tabKey="sale_quotations"><SaleQuotationForm /></TabRoute>} />
              <Route path="sale-quotations/:id"      element={<TabRoute tabKey="sale_quotations"><SaleQuotationPrint /></TabRoute>} />
              <Route path="sale-quotations/:id/edit" element={<TabRoute tabKey="sale_quotations"><SaleQuotationForm /></TabRoute>} />

              <Route path="quotations"          element={<TabRoute tabKey="quotations"><QuotationList /></TabRoute>} />
              <Route path="quotations/new"      element={<TabRoute tabKey="quotations"><QuotationForm /></TabRoute>} />
              <Route path="quotations/:id"      element={<TabRoute tabKey="quotations"><QuotationPrint /></TabRoute>} />
              <Route path="quotations/:id/edit" element={<TabRoute tabKey="quotations"><QuotationForm /></TabRoute>} />

              <Route path="users"          element={<TabRoute tabKey="users"><UserList /></TabRoute>} />
              <Route path="settings/terms"    element={<TabRoute tabKey="terms"><TermsAdmin /></TabRoute>} />
              <Route path="settings/company" element={<TabRoute tabKey="terms"><CompanySettings /></TabRoute>} />

              <Route path="agent" element={<AgentRoute><AgentPortal /></AgentRoute>} />
            </Route>
          </Routes>
        </RecoveryGate>
        </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
  );
}
